import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Bell, Mail, Smartphone, Settings } from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationSettingsPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    email_enabled: true,
    email_on_assignment: true,
    email_on_status_change: true,
    email_on_time_approval: true,
    push_enabled: false,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/notifications/settings');
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [api]);

  const handleToggle = async (key, value) => {
    setSaving(true);
    try {
      await api.patch('/notifications/settings', { [key]: value });
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success(t('settings.saved'));
    } catch (err) {
      toast.error(t('settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePush = async () => {
    if (!('Notification' in window)) {
      toast.error(t('notifications.pushNotSupported'));
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        // In a real app, you'd register with a push service here
        await api.patch('/notifications/settings', { push_enabled: true });
        setSettings(prev => ({ ...prev, push_enabled: true }));
        toast.success(t('notifications.pushEnabled'));
      } else {
        toast.error(t('notifications.pushDenied'));
      }
    } catch (err) {
      toast.error(t('notifications.pushFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('notifications.settingsTitle')}</h1>
        <p className="text-muted-foreground mt-1">{t('notifications.settingsSubtitle')}</p>
      </div>

      {/* Email Notifications */}
      <Card className="cgc-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5" />
            {t('notifications.emailNotifications')}
          </CardTitle>
          <CardDescription>{t('notifications.emailDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t('notifications.emailEnabled')}</Label>
              <p className="text-sm text-muted-foreground">{t('notifications.emailEnabledDesc')}</p>
            </div>
            <Switch
              checked={settings.email_enabled}
              onCheckedChange={(v) => handleToggle('email_enabled', v)}
              disabled={saving}
              data-testid="email-enabled-switch"
            />
          </div>

          {settings.email_enabled && (
            <>
              <div className="flex items-center justify-between pl-4 border-l-2 border-muted">
                <div className="space-y-0.5">
                  <Label>{t('notifications.onAssignment')}</Label>
                  <p className="text-sm text-muted-foreground">{t('notifications.onAssignmentDesc')}</p>
                </div>
                <Switch
                  checked={settings.email_on_assignment}
                  onCheckedChange={(v) => handleToggle('email_on_assignment', v)}
                  disabled={saving}
                  data-testid="email-assignment-switch"
                />
              </div>

              <div className="flex items-center justify-between pl-4 border-l-2 border-muted">
                <div className="space-y-0.5">
                  <Label>{t('notifications.onStatusChange')}</Label>
                  <p className="text-sm text-muted-foreground">{t('notifications.onStatusChangeDesc')}</p>
                </div>
                <Switch
                  checked={settings.email_on_status_change}
                  onCheckedChange={(v) => handleToggle('email_on_status_change', v)}
                  disabled={saving}
                  data-testid="email-status-switch"
                />
              </div>

              <div className="flex items-center justify-between pl-4 border-l-2 border-muted">
                <div className="space-y-0.5">
                  <Label>{t('notifications.onTimeApproval')}</Label>
                  <p className="text-sm text-muted-foreground">{t('notifications.onTimeApprovalDesc')}</p>
                </div>
                <Switch
                  checked={settings.email_on_time_approval}
                  onCheckedChange={(v) => handleToggle('email_on_time_approval', v)}
                  disabled={saving}
                  data-testid="email-time-switch"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card className="cgc-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Smartphone className="h-5 w-5" />
            {t('notifications.pushNotifications')}
          </CardTitle>
          <CardDescription>{t('notifications.pushDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t('notifications.pushEnabled')}</Label>
              <p className="text-sm text-muted-foreground">{t('notifications.pushEnabledDesc')}</p>
            </div>
            {settings.push_enabled ? (
              <Switch
                checked={true}
                onCheckedChange={(v) => handleToggle('push_enabled', v)}
                disabled={saving}
                data-testid="push-enabled-switch"
              />
            ) : (
              <Button variant="outline" size="sm" onClick={handleEnablePush}>
                <Bell className="h-4 w-4 mr-2" />
                {t('notifications.enablePush')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
